package ui

/**
 * Retains an authenticated identity while live services are still starting.
 *
 * The first attempt invokes [authenticate] once. Later attempts call only
 * [continueAuthenticated] with that retained identity.
 *
 * @param T Authenticated user value.
 * @param C UI or workflow context passed to the continuation.
 * @param authenticate Completes OAuth and returns the authenticated identity.
 * @param continueAuthenticated Continues setup for an already authenticated user.
 */
internal class AuthenticatedReadinessContinuation<T : Any, C>(
    private val authenticate: suspend () -> T,
    private val continueAuthenticated: suspend (T, C) -> Unit,
)
{
    private var authenticatedUser: T? = null

    /** Authenticates once, retains the identity, then continues startup. */
    suspend fun start(context: C)
    {
        check(authenticatedUser == null) { "login has already authenticated" }
        val user = authenticate()
        authenticatedUser = user
        continueAuthenticated(user, context)
    }

    /** Retries startup with the retained identity without repeating OAuth. */
    suspend fun retry(context: C)
    {
        val user = authenticatedUser ?: error("cannot retry before authentication succeeds")
        continueAuthenticated(user, context)
    }
}

/** Returns the user-facing cold-start message for the live readiness gate. */
internal fun liveServicesStartupMessage(timeoutMillis: Long): String
{
    val timeoutSeconds = (timeoutMillis + 999L) / 1_000L
    return "Starting live game services. Waiting up to ${timeoutSeconds}s."
}

/** Returns the user-facing readiness progress message. */
internal fun liveServicesProgressMessage(elapsedMillis: Long, timeoutMillis: Long): String
{
    val elapsedSeconds = elapsedMillis / 1_000L
    val timeoutSeconds = (timeoutMillis + 999L) / 1_000L
    return "Starting live game services. ${elapsedSeconds}s elapsed; waiting up to ${timeoutSeconds}s."
}

/** Returns the retry prompt shown while preserving the completed OAuth login. */
internal fun liveServicesRetryMessage(error: String?): String
{
    return "${error ?: "Live services are still starting."} " +
        "You are still signed in. Press OK to retry without logging in again."
}
